import { useEffect, useMemo, useState } from 'react';
import { Database, Plus, RotateCcw, Search } from 'lucide-react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useCreateDataset, useDatasets, useDeleteDataset, useUpdateDataset } from '@/api/queries';
import type { DatasetSummaryDto } from '@/api/types';
import { DatasetList } from '@/components/datasets/DatasetList';
import { DatasetModal } from '@/components/datasets/DatasetModal';
import { DeleteConfirmModal } from '@/components/datasets/DeleteConfirmModal';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { ProcessingIndicator } from '@/components/ui/ProcessingIndicator';
import styles from './DataIndexPage.module.css';
import { CREATE_SOURCE_PARAMS, routes, withoutParams } from '@/lib/routePaths';

type StatusFilter = 'All' | 'Ready' | 'Failed';

export default function DataIndexPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: datasetsList, isLoading, error, refetch } = useDatasets();
  const createMutation = useCreateDataset();
  const updateMutation = useUpdateDataset();
  const deleteMutation = useDeleteDataset();

  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All');
  const [modalMode, setModalMode] = useState<'edit' | null>(null);
  const [modalDataset, setModalDataset] = useState<DatasetSummaryDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DatasetSummaryDto | null>(null);
  const [busyDatasetId, setBusyDatasetId] = useState<string | null>(null);
  const [message, setMessage] = useState<{ kind: 'success' | 'danger'; text: string } | null>(() => {
    const notice = (location.state as { notice?: string } | null)?.notice;
    return notice ? { kind: 'success', text: notice } : null;
  });

  useEffect(() => {
    if ((location.state as { notice?: string } | null)?.notice) {
      navigate(`${routes.datasets}${location.search}`, { replace: true, state: null });
    }
  }, [location.state, navigate]);

  const datasets = useMemo(() => sortDatasets(datasetsList ?? []), [datasetsList]);

  const filteredDatasets = useMemo(() => {
    return filterDatasets(datasets, query, statusFilter);
  }, [datasets, query, statusFilter]);

  async function handleCreate(data: FormData) {
    const result = await createMutation.mutateAsync(data);
    if (result.success && result.dataset?.id) {
      navigate(routes.dataset(result.dataset.id));
    }
    return result;
  }

  async function handleUpdate(data: FormData, dataset: DatasetSummaryDto) {
    const id = dataset.id ?? '';
    setBusyDatasetId(id);
    try {
      const result = await updateMutation.mutateAsync({ id, formData: data });
      if (result.success) {
        setMessage({ kind: 'success', text: `Dataset "${result.dataset?.displayName ?? ''}" updated.` });
      }
      return result;
    } finally {
      setBusyDatasetId(null);
    }
  }

  async function handleDelete(dataset: DatasetSummaryDto) {
    const id = dataset.id ?? '';
    setDeleteTarget(null);
    setBusyDatasetId(id);
    try {
      await deleteMutation.mutateAsync(id);
      setMessage({ kind: 'success', text: `Deleted ${dataset.displayName ?? 'dataset'}.` });
    } catch (deleteError) {
      setMessage({
        kind: 'danger',
        text: deleteError instanceof Error ? deleteError.message : 'Could not delete the dataset.',
      });
    } finally {
      setBusyDatasetId(null);
    }
  }

  function openCreateModal() {
    setMessage(null);
    setModalDataset(null);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('create', '1');
      return next;
    });
  }

  function closeCreateModal() {
    setSearchParams((current) => withoutParams(current, CREATE_SOURCE_PARAMS), { replace: true });
  }

  function openEditModal(dataset: DatasetSummaryDto) {
    setMessage(null);
    setModalMode('edit');
    setModalDataset(dataset);
  }

  function clearFilters() {
    setQuery('');
    setStatusFilter('All');
  }

  return (
    <section className={styles.page} aria-labelledby="data-title">
      <header className={styles.pageHeader}>
        <div>
          <div className={styles.titleLine}>
            <h1 id="data-title">Datasets</h1>
            {!isLoading && <span className={styles.count}>{datasets.length.toLocaleString()}</span>}
          </div>
          <p>Manage market datasets and open their candle and news history.</p>
        </div>
        <Button variant="primary" onClick={openCreateModal}>
          <Plus size={16} /> Add dataset
        </Button>
      </header>

      {message && <Alert variant={message.kind}>{message.text}</Alert>}

      {isLoading ? (
        <ProcessingIndicator label="Loading datasets" />
      ) : error ? (
        <div className={styles.statePanel}>
          <Database size={24} />
          <h2>Could not load datasets</h2>
          <p>{error.message}</p>
          <Button type="button" variant="outline-info" onClick={() => void refetch()}>
            <RotateCcw size={15} /> Retry
          </Button>
        </div>
      ) : datasets.length === 0 ? (
        <div className={styles.statePanel}>
          <Database size={28} />
          <h2>No datasets yet</h2>
          <p>Add 1-minute candle and news CSV files to start exploring market data.</p>
          <Button type="button" variant="primary" onClick={openCreateModal}>
            <Plus size={16} /> Add dataset
          </Button>
        </div>
      ) : (
        <>
          <div className={styles.filters}>
            <label className={styles.searchField}>
              <span>Search datasets</span>
              <div className={styles.inputShell}>
                <Search size={15} />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Name or instrument"
                />
              </div>
            </label>
            <label className={styles.statusField}>
              <span>Status</span>
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}>
                <option value="All">All statuses</option>
                <option value="Ready">Ready</option>
                <option value="Failed">Failed</option>
              </select>
            </label>
            <span className={styles.resultCount}>{filteredDatasets.length.toLocaleString()} shown</span>
          </div>

          {filteredDatasets.length === 0 ? (
            <div className={styles.statePanel}>
              <Search size={24} />
              <h2>No matching datasets</h2>
              <p>Try a different name, instrument, or status.</p>
              <Button type="button" variant="outline-info" onClick={clearFilters}>Clear filters</Button>
            </div>
          ) : (
            <DatasetList
              datasets={filteredDatasets}
              onEdit={openEditModal}
              onDelete={setDeleteTarget}
              busyDatasetId={busyDatasetId}
            />
          )}
        </>
      )}

      {searchParams.get('create') === '1' && (
        <DatasetModal
          mode="create"
          dataset={null}
          open
          onClose={closeCreateModal}
          onSubmit={handleCreate}
          onSuccess={() => { /* Successful creation navigates to the canonical detail route. */ }}
        />
      )}

      {modalMode === 'edit' && modalDataset && (
        <DatasetModal
          mode="edit"
          dataset={modalDataset}
          open
          onClose={() => setModalMode(null)}
          onSubmit={(data) => handleUpdate(data, modalDataset)}
          onSuccess={() => setModalMode(null)}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          dataset={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => handleDelete(deleteTarget)}
        />
      )}
    </section>
  );
}

export function sortDatasets(datasets: readonly DatasetSummaryDto[]) {
  return [...datasets].sort((left, right) =>
    Date.parse(right.createdUtc ?? '') - Date.parse(left.createdUtc ?? ''),
  );
}

export function filterDatasets(datasets: readonly DatasetSummaryDto[], query: string, status: StatusFilter) {
  const normalizedQuery = query.trim().toLowerCase();
  return datasets.filter((dataset) => {
    const matchesQuery = normalizedQuery.length === 0
      || dataset.displayName?.toLowerCase().includes(normalizedQuery)
      || dataset.instrument?.toLowerCase().includes(normalizedQuery);
    const matchesStatus = status === 'All' || dataset.status === status;
    return matchesQuery && matchesStatus;
  });
}
